import { useEffect, useState } from "react";
import { Link } from "../components/LocaleLink";
import { ArrowRight } from "lucide-react";
import { getBlogPosts } from "../data/blog";
import { useLang } from "../context/LanguageContext";
import { optimizeImg, fetchBlogList, localized, resolveMediaUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";

export default function BlogList() {
  const { lang, t } = useLang();
  const locale = t("blogUi.locale");
  const [posts, setPosts] = useState(() => getBlogPosts(lang));

  useEffect(() => {
    let active = true;
    const staticPosts = getBlogPosts(lang);
    fetchBlogList()
      .then((dbPosts) => {
        if (!active) return;
        const normalized = dbPosts.map((p) => ({
          slug: p.slug,
          title: localized(p.title, lang),
          excerpt: localized(p.excerpt, lang),
          image: p.image,
          date: p.date,
        }));
        const seen = new Set(normalized.map((p) => p.slug));
        const merged = [...normalized, ...staticPosts.filter((p) => !seen.has(p.slug))];
        merged.sort((a, b) => new Date(b.date) - new Date(a.date));
        setPosts(merged);
      })
      .catch(() => setPosts(staticPosts));
    return () => { active = false; };
  }, [lang]);

  return (
    <div data-testid="blog-list-page" className="pt-36 md:pt-44 pb-24 md:pb-32">
      <Seo title={t("blogUi.seoTitle")} description={t("blogUi.seoDescription")} />
      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1100px]">
        <Reveal>
          <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary">{t("blogUi.overline")}</p>
          <h1 className="mt-5 font-headings text-5xl sm:text-6xl leading-[1.05] tracking-tight text-foreground">{t("blogUi.title")}</h1>
          <p className="mt-5 font-body font-light text-lg text-foreground/70 max-w-2xl">{t("blogUi.subtitle")}</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-10" data-testid="blog-grid">
          {posts.map((post, i) => (
            <Reveal key={post.slug} delay={(i % 2) * 0.08}>
              <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`} className="group block border border-border/50 hover:border-primary/60 transition-colors duration-300">
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={post.image ? resolveMediaUrl(optimizeImg(post.image, 900)) : "/logo-velmira.jpg"} alt={post.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-7">
                  <span className="font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{new Date(post.date).toLocaleDateString(locale, { year: "numeric", month: "long", day: "numeric" })}</span>
                  <h2 className="mt-3 font-headings text-2xl leading-snug text-foreground group-hover:text-primary transition-colors">{post.title}</h2>
                  <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{post.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-primary">{t("blogUi.readArticle")} <ArrowRight size={14} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        {/* Türkçe / Turkish content section */}
        <TurkishSection />
      </div>
    </div>
  );
}

const TURKISH_LINKS = [
  { to: "/nice-gayrimenkul", title: "Nice Gayrimenkul", desc: "Türk yatırımcılar için satın alma süreci, getiri ve yönetim." },
  { to: "/nice-emlak", title: "Nice Emlak", desc: "Daire, villa ve yatırımlık konut — güncel fiyat aralıkları." },
  { to: "/nice-ev-satin-alma-rehberi", title: "Nice'te Ev Satın Alma Rehberi", desc: "Teklif → compromis → notaire → acte de vente adım adım." },
  { to: "/fransa-gayrimenkul-yatirimi", title: "Fransa'da Gayrimenkul Yatırımı", desc: "Kira getirisi, LMNP / SCI ve Côte d'Azur pazar dinamikleri." },
  { to: "/turkler-icin-nice-rehberi", title: "Türkler İçin Nice Rehberi", desc: "Yaşam maliyeti, semtler, oturum, okullar, sağlık ve uçuşlar." },
  { to: "/nice-oturum-izni-gayrimenkul", title: "Nice'te Oturum İzni ve Gayrimenkul", desc: "VLS-TS visiteur, mülk sahipliğinin başvuruya etkisi." },
  { to: "/nice-airbnb-yatirimi", title: "Nice'te Airbnb Yatırımı", desc: "Getiri, LMNP yapısı, düzenlemeler ve tam kapsamlı yönetim." },
  { to: "/cote-dazur-luks-konutlar", title: "Côte d'Azur'da Lüks Konutlar", desc: "Cap Ferrat, Villefranche, Mont Boron, Cannes, Monaco." },
  { to: "/nice-vs-istanbul", title: "Nice mi İstanbul mu?", desc: "Likidite, getiri, para birimi ve yaşam karşılaştırması." },
  { to: "/nice-vs-dubai", title: "Nice mi Dubai mi?", desc: "Sermaye koruması, kira getirisi ve vergi karşılaştırması." },
  { to: "/nice-vs-bodrum", title: "Nice mi Bodrum mu?", desc: "İkinci ev karşılaştırması: sezon, likidite, euro varlığı." },
  { to: "/nice-vs-lizbon", title: "Nice mi Lizbon mu?", desc: "Avrupa'da yatırım: vize, olgunluk, likidite." },
];

function TurkishSection() {
  return (
    <section data-testid="blog-turkish-section" className="mt-24 pt-16 border-t border-border/60">
      <Reveal>
        <div className="flex items-baseline gap-3">
          <p className="font-accent text-xs uppercase tracking-[0.25em] text-primary">Türkçe</p>
          <span className="text-foreground/40 text-xs font-body">Turkish resources</span>
        </div>
        <h2 className="mt-4 font-headings text-3xl sm:text-4xl leading-[1.1] tracking-tight text-foreground">Türk alıcılar için rehberler</h2>
        <p className="mt-4 font-body font-light text-base text-foreground/70 max-w-2xl">Nice ve Côte d'Azur'da gayrimenkul, Airbnb yatırımı, oturum izni ve karşılaştırmalar için Türkçe kaynaklarımız — Türk vatandaşları için özel olarak hazırlandı.</p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="blog-turkish-grid">
        {TURKISH_LINKS.map((l, i) => (
          <Reveal key={l.to} delay={(i % 3) * 0.05}>
            <Link
              to={l.to}
              data-testid={`turkish-link-${l.to.slice(1)}`}
              className="group block border border-border/50 hover:border-primary/60 bg-secondary/40 p-5 h-full transition-colors duration-300"
            >
              <h3 className="font-headings text-lg text-foreground group-hover:text-primary transition-colors leading-snug">{l.title}</h3>
              <p className="mt-2 font-body text-sm text-foreground/65 leading-relaxed">{l.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 font-accent text-[10px] uppercase tracking-[0.2em] text-primary">Oku <ArrowRight size={12} /></span>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}
